<template>
    <div>
        <div class="snapshot-chart p-3">
            <div v-if="!isMinimalView">
                <div class="mb-2 d-flex">
                    <div>
                        <div class="chart-title d-flex">
                            <span v-if="titleIcon" class="mr-2">
                                <i
                                    v-if="!isSvgIcon(titleIcon)"
                                    :class="titleIcon"
                                ></i>
                                <span
                                    v-else
                                    v-html="titleIcon"
                                    class="svg-icon-wrapper"
                                ></span>
                            </span>
                            <p class="">{{ title }}</p>
                        </div>
                        <div>
                            <span class="">{{ description }}</span>
                        </div>
                    </div>
                    <div class="align-items-end d-flex flex-column flex-fill">
                        <div>
                            <button
                                class="btn btn-link p-0 mr-3 cursor-pointer mb-2"
                                @click.stop="onChartClick"
                                :title="`View ${title} vital list`"
                            >
                                <i class="fas fa-external-link-alt"></i>
                            </button>
                            <button
                                class="btn btn-link p-0 mb-2"
                                @click.stop="toggleView"
                                :title="
                                    isMinimalView
                                        ? 'Show full view'
                                        : 'Show minimal view'
                                "
                            >
                                <i
                                    :class="
                                        isMinimalView
                                            ? 'fas fa-chevron-down'
                                            : 'fas fa-chevron-up'
                                    "
                                ></i>
                            </button>
                        </div>
                        <h2 class="mb-0 pb-1 text-nowrap text-right">
                            {{ value }}
                        </h2>
                    </div>
                </div>
                <div>
                    <Chart
                        v-if="chartData.length > 0"
                        :chart-id="id"
                        :data="chartData"
                        :height="120"
                        :ranges="ranges"
                        :options="chartOptions"
                        :xaxis-labels="xaxisLabels"
                    />
                </div>
            </div>
            <div v-else class="trend-line d-flex align-items-center">
                <div class="d-flex flex-fill align-items-center">
                    <span v-if="titleIcon" class="mr-2 chart-title">
                        <i v-if="!isSvgIcon(titleIcon)" :class="titleIcon"></i>
                        <span
                            v-else
                            v-html="titleIcon"
                            class="svg-icon-wrapper"
                        ></span>
                    </span>
                    <p class="mb-0 mr-2 chart-title">{{ title }}</p>
                    <p class="mb-0 font-weight-bold text-nowrap">
                        {{ value }}
                    </p>
                </div>
                <div class="chart-container">
                    <Chart
                        v-if="chartData.length > 0"
                        :chart-id="`${id}-minimal`"
                        :data="chartData"
                        :height="20"
                        :ranges="ranges"
                        :options="minimalChartOptions"
                        :xaxis-labels="xaxisLabels"
                    />
                </div>
                <button
                    class="btn btn-link p-0 mr-3"
                    @click.stop="onChartClick"
                    :title="`View ${title} vital list`"
                >
                    <i class="fas fa-external-link-alt"></i>
                </button>
                <button
                    class="btn btn-link p-0"
                    @click.stop="toggleView"
                    :title="
                        isMinimalView ? 'Show full view' : 'Show minimal view'
                    "
                >
                    <i
                        :class="
                            isMinimalView
                                ? 'fas fa-chevron-down'
                                : 'fas fa-chevron-up'
                        "
                    ></i>
                </button>
            </div>
            <!-- <div v-else class="trend-line">
                <Chart
                    v-if="chartData.length > 0"
                    :chart-id="`${id}-minimal`"
                    :data="chartData"
                    :height="40"
                    :ranges="ranges"
                    :options="minimalChartOptions"
                    :xaxis-labels="xaxisLabels"
                />
            </div> -->
        </div>
    </div>
</template>

<script>
import Chart from "@/components/commons/chart.vue";

export default {
    name: "SnapshotChart",
    components: {
        Chart
    },
    props: {
        chartData: {
            type: Array,
            required: true
        },
        xaxisLabels: {
            type: Array,
            required: true
        },
        id: {
            type: String,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        titleIcon: {
            type: String,
            required: false,
            default: ""
        },
        value: {
            type: String,
            required: false
        },
        description: {
            type: String,
            required: false
        },
        unit: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            ranges: [],
            isMinimalView: true
        };
    },
    computed: {
        minimalChartOptions() {
            const options = {
                ...this.chartOptions,
                series: {
                    ...this.chartOptions.series,
                    points: {
                        show: false
                    }
                },
                grid: {
                    ...this.chartOptions.grid,
                    margin: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0
                    },
                    borderWidth: 0
                },
                xaxis: {
                    ...this.chartOptions.xaxis,
                    show: false
                },
                yaxis: {
                    ...this.chartOptions.yaxis,
                    ticks: [],
                    show: false
                },
                tooltip: false
            };
            return options;
        },
        chartOptions() {
            const options = {
                series: {
                    lines: {
                        show: true,
                        color: "#000000"
                    },
                    points: {
                        show: true,
                        radius: 3,
                        fillColor: "#000000",
                        color: "#000000"
                    }
                },
                grid: {
                    borderWidth: 1,
                    borderColor: {
                        top: "transparent",
                        right: "transparent",
                        bottom: "#B9B9B9",
                        left: "#B9B9B9"
                    },
                    hoverable: true,
                    margin: {
                        top: 20,
                        right: 40,
                        bottom: 0,
                        left: 0
                    }
                },
                xaxis: {
                    rotateTicks: 0,
                    tickColor: "#B9B9B9"
                },
                yaxis: {
                    tickColor: "transparent",
                    tickSize: 50,
                    tickDecimals: 0
                },
                tooltip: true,
                tooltipOpts: {
                    content: (label, x, y, flotItem) => {
                        let plainLabel = "";
                        if (
                            this.xaxisLabels &&
                            this.xaxisLabels[x] &&
                            typeof this.xaxisLabels[x][1] === "string"
                        ) {
                            // Get the original date from the data
                            const originalDate = this.chartData[x][2]?.date;
                            if (originalDate) {
                                const dateString = this._carbonDateDisplay(
                                    originalDate,
                                    "MMM dd, yyyy"
                                );
                                plainLabel = dateString;
                            } else {
                                let htmlLabel = this.xaxisLabels[x][1];
                                const tempDiv = document.createElement("div");
                                tempDiv.innerHTML = htmlLabel;
                                plainLabel =
                                    tempDiv.textContent ||
                                    tempDiv.innerText ||
                                    "";
                            }
                        }
                        return `${plainLabel}<br/>${y} ${
                            this.unit || ""
                        }`.trim();
                    },
                    shifts: { x: 20, y: 0 },
                    defaultTheme: false
                }
            };

            // If there is only one data point, show the point and hide the line
            // if there is more than one data point, show the line and hide the point
            if (this.chartData && this.chartData.length === 1) {
                options.series.lines.show = false;
                options.series.points.show = true;
            } else {
                options.series.lines.show = true;
                options.series.points.show = true;

                const yValues = this.chartData.map(point => point[1]);

                // Calculate min, max, and middle
                const min = Math.min(...yValues);
                const minValue = min > 0 ? 0 : min;
                const max = Math.max(...yValues);
                const middle = (minValue + max) / 2;

                // show only min, middle, max on y-axis
                options.yaxis.ticks = [minValue, middle, max];
            }

            return options;
        }
    },
    methods: {
        isSvgIcon(iconString) {
            return (
                typeof iconString === "string" &&
                iconString.trim().startsWith("<svg")
            );
        },
        onChartClick() {
            this.$emit("chart-clicked", this.id);
        },
        toggleView(event) {
            event.stopPropagation();
            this.isMinimalView = !this.isMinimalView;
            this.saveViewPreference();
        },
        saveViewPreference() {
            const staffDetails = this.$root.currentActor;
            const userId = staffDetails?.id || "anonymous";
            const key = `chart-view-${userId}-${this.id}`;
            localStorage.setItem(key, JSON.stringify(this.isMinimalView));
        },
        loadViewPreference() {
            const staffDetails = this.$root.currentActor;
            const userId = staffDetails?.id || "anonymous";
            const key = `chart-view-${userId}-${this.id}`;
            const savedPreference = localStorage.getItem(key);
            if (savedPreference !== null) {
                this.isMinimalView = JSON.parse(savedPreference);
            }
        }
    },
    mounted() {
        this.loadViewPreference();
    }
};
</script>

<style scoped>
.snapshot-chart {
    width: 100%;
    background-color: #e8ecff;
}

.svg-icon-wrapper {
    display: inline-flex; /* Or inline-block */
    align-items: center;
    justify-content: center;
    width: 1.2em;
    height: 1.2em;
    vertical-align: middle;
}

.svg-icon-wrapper :deep(svg) {
    width: 100%;
    height: 100%;
    display: block;
}

.btn-link {
    color: #666;
    text-decoration: none;
}

.btn-link:hover {
    color: #000;
}

.chart-title {
    font-size: 12px;
}

.chart-container {
    width: 25%;
    margin-right: 0.7rem;
}

@media (min-width: 1200px) {
    .chart-container {
        width: 60px;
    }
}
</style>
